// @core/stores/calendar/CalendarStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { calendarApi } from '@core/api';
import type {
  AddCalendarMemberRequest,
  Calendar,
  CalendarMemberResponse,
  CreateCalendarRequest,
  ExportCalendarResponse,
  ImportCalendarRequest,
  ImportCalendarResponse,
  ListCalendarMembersResponse,
  UpdateCalendarMemberRoleRequest,
  UpdateCalendarRequest,
} from '@core/types/calendar';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class CalendarStore {
  // List state
  items: Calendar[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Calendar | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Calendar>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Calendar | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Calendar | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add calendar member
   */
  async AddCalendarMember(calendar_id: string, data: AddCalendarMemberRequest): Promise<CalendarMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.AddCalendarMember(calendar_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create calendar
   */
  async CreateCalendar(data: CreateCalendarRequest): Promise<Calendar> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.CreateCalendar(data);
      const item = response.calendar;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete calendar
   */
  async DeleteCalendar(calendar_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await calendarApi.DeleteCalendar(calendar_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== calendar_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === calendar_id) {
          this.currentItem = null;
        }

        this.removeFromCache(calendar_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Export calendar
   */
  async ExportCalendar(calendar_id: string): Promise<ExportCalendarResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.ExportCalendar(calendar_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get calendar
   */
  async GetCalendar(calendar_id: string): Promise<Calendar> {
    // Check cache first
    const cached = this.cache.get(calendar_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await calendarApi.GetCalendar(calendar_id);
      const item = response.calendar;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Import calendar
   */
  async ImportCalendar(calendar_id: string, data: ImportCalendarRequest): Promise<ImportCalendarResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.ImportCalendar(calendar_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List calendar members
   */
  async ListCalendarMembers(calendar_id: string, params?: PaginatedRequest): Promise<ListCalendarMembersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.ListCalendarMembers(calendar_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List calendars
   */
  async ListCalendars(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await calendarApi.ListCalendars(params);

      runInAction(() => {
        this.items = response.calendars?.calendars ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove calendar member
   */
  async RemoveCalendarMember(calendar_id: string, member_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.RemoveCalendarMember(calendar_id, member_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update calendar
   */
  async UpdateCalendar(calendar_id: string, data: UpdateCalendarRequest): Promise<Calendar> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.UpdateCalendar(calendar_id, data);
      const item = response.calendar;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update member role
   */
  async UpdateCalendarMemberRole(calendar_id: string, member_id: string, data: UpdateCalendarMemberRoleRequest): Promise<CalendarMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await calendarApi.UpdateCalendarMemberRole(calendar_id, member_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const calendarStore = new CalendarStore();
